import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setToken } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [actor, setActor] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  const load = useCallback(async () => {
    // A transient failure (restart, dropped connection) must not sign the user
    // out of a live console. Only a real 401 clears the session.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const payload = await api.get('/auth/me');
        setActor(payload.data.actor);
        setProfile(payload.data.profile);
        setOffline(false);
        setLoading(false);
        return;
      } catch (err) {
        if (err.status === 401) {
          setActor(null);
          setProfile(null);
          setOffline(false);
          setLoading(false);
          return;
        }
        if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 1200));
      }
    }
    // Still unreachable: keep whoever was signed in and flag the connection.
    setOffline(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = useCallback(async (email, password) => {
    const payload = await api.post('/auth/login', { email, password });
    setToken(payload.data.token);
    setActor(payload.data.actor);
    setOffline(false);
    return payload.data.actor;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    setToken(null);
    setActor(null);
    setProfile(null);
    setOffline(false);
  }, []);

  const value = useMemo(
    () => ({ actor, profile, loading, offline, login, logout, reload: load }),
    [actor, profile, loading, offline, login, logout, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
